import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './FilterOptions.module.scss';
import { useTranslation } from 'react-i18next';
import Icon from '../../../common/Icon/Icon';
import { useAppSelector } from '../../../../redux/hooks';
import { FilterKey, useFiltersContext } from '../FilterContext/FilterContext';
import useClickOutside from '../../../CustomHooks/useClickOutside';
import Checkbox from '../Checkbox/Checkbox';
import {
  useGetAMCEandEmaraldAMQuery,
  useGetloaFilterQuery,
  useGetOptionMasterFilterQuery,
  useGetUplineDiamondEmraldQuery,
} from '../../../../redux/services/aboFilterAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import { filterStateValuesObject } from '../../../../types/types';
import { RootState } from '../../../../redux/store';
interface Option {
  value: string;
  checked: boolean;
  code: string;
}

const typeIdMap: Record<string, string> = {
  primaryAM: '3',
  caseEvaluator: '4',
  amEmerald: '3',
};

const mapOptionsFromState = (
  options: filterStateValuesObject[],
  selectedOptions: string[]
): Option[] => {
  const mappedOptions = options.map<Option>((item) => ({
    value: item?.value,
    checked: selectedOptions?.includes(item?.code),
    code: item?.code,
  }));
  return mappedOptions;
};

interface FilterOptionsProps {
  closingMonth: string;
}

const FilterOptions: React.FC<FilterOptionsProps> = ({ closingMonth }) => {
  const { selectedFilter } = useFiltersContext();
  const selectedTypeId = typeIdMap[selectedFilter] ?? null;
  const isUplineFilter = selectedFilter === 'uplineDiamond' || selectedFilter === 'uplineEmerald';
  const awardListEligible = selectedFilter === 'trackingAward';
  const stateListEligible = selectedFilter === 'state';
  const languageListEligible = selectedFilter === 'language';
  const marketListEligible = selectedFilter === 'marketName';
  const loaListEligible = selectedFilter === 'loa';
  const userDetails = useAppSelector((state: RootState) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const [month, year] = closingMonth.split('/');
  const formattedYearMonth = `${year}${month.padStart(2, '0')}`;
  const [userCountryCode] = useState(userDetails?.userCountryCode);

  const {
    data: AMCEList,
    isLoading: isAMCELoading,
    isFetching: isAMCEFetching,
  } = useGetAMCEandEmaraldAMQuery(
    selectedTypeId
      ? {
          code: 'AMW',
          typeId: selectedTypeId,
          affiliateCode: userAffiliateCode,
        }
      : skipToken
  );

  const { data: uplineDiamondEmaraldList, isFetching: isUplineLoading } =
    useGetUplineDiamondEmraldQuery(
      isUplineFilter
        ? {
            uplineName: selectedFilter === 'uplineDiamond' ? 'uplinediamond' : 'uplineemerald',
            month: formattedYearMonth + '',
          }
        : skipToken
    );

  const {
    data: awardList,
    isLoading: isAwardListLoading,
    isFetching: isAwardListFetching,
  } = useGetOptionMasterFilterQuery(
    awardListEligible
      ? {
          affiliateCode: userAffiliateCode,
          filterTypeName: 'aboassignment-award',
        }
      : skipToken
  );
  const {
    data: stateList,
    isLoading: isStateListLoading,
    isFetching: isStateListFetching,
  } = useGetOptionMasterFilterQuery(
    stateListEligible
      ? {
          affiliateCode: userAffiliateCode,
          filterTypeName: 'aboassignment-state',
        }
      : skipToken
  );
  const {
    data: languageList,
    isLoading: isLanguageListLoading,
    isFetching: isLanguageListFetching,
  } = useGetOptionMasterFilterQuery(
    languageListEligible
      ? {
          affiliateCode: userAffiliateCode,
          filterTypeName: 'aboassignment-language',
        }
      : skipToken
  );
  const {
    data: marketList,
    isLoading: isMarketListLoading,
    isFetching: isMarketListFetching,
  } = useGetOptionMasterFilterQuery(
    marketListEligible
      ? {
          affiliateCode: userAffiliateCode,
          filterTypeName: 'aboassignment-market',
        }
      : skipToken
  );
  const {
    data: loaList,
    isLoading: isLoaListLoading,
    isFetching: isLoaListFetching,
  } = useGetloaFilterQuery(
    loaListEligible && userCountryCode !== 'IN'
      ? {
          closingMonth: formattedYearMonth + '',
        }
      : skipToken
  );

  switch (selectedFilter) {
    case 'trackingAward':
    case 'currentAward':
      return <OptionsWithTabs AMList={awardList || []} isLoading={isAwardListFetching} />;

    case 'primaryAM':
    case 'caseEvaluator':
    case 'amEmerald':
      return <OptionsWithSearch AMList={AMCEList || []} isLoading={isAMCEFetching} />;

    case 'uplineDiamond':
    case 'uplineEmerald':
      return (
        <OptionsWithSearch AMList={uplineDiamondEmaraldList || []} isLoading={isUplineLoading} />
      );
    case 'state':
      return <OptionsWrapper AMList={stateList || []} isLoading={isStateListFetching} />;
    case 'language':
      return <OptionsWrapper AMList={languageList} isLoading={isLanguageListFetching} />;
    case 'marketName':
      return <OptionsWrapper AMList={marketList || []} isLoading={isMarketListFetching} />;
    case 'loa':
      return <OptionsWrapper AMList={loaList || []} isLoading={isLoaListFetching} />;
    default:
      return <OptionsWrapper AMList={AMCEList || []} isLoading={isAMCEFetching} />;
  }
};

const OptionsWithTabs: React.FC<{ AMList?: any; isLoading?: boolean }> = ({
  AMList,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { selectedFilter, setSelectedFilter } = useFiltersContext();

  return (
    <>
      <div className={`tab_wrapper ${styles.tab_wrapper}`}>
        <div className={`tabs ${styles.tabs}`}>
          <div
            className={`${styles.tab} ${selectedFilter === 'trackingAward' ? styles.active : ''}`}
            onClick={() => setSelectedFilter('trackingAward')}
          >
            {t('tab_tracking_award')}
          </div>
          <div
            className={`tab ${styles.tab} ${selectedFilter === 'currentAward' ? styles.active : ''}`}
            onClick={() => setSelectedFilter('currentAward')}
          >
            {t('tab_current_award')}
          </div>
        </div>
      </div>
      <div className={`tab_content ${styles.tab_content}`}>
        <OptionsWrapper AMList={AMList} isLoading={isLoading} />
      </div>
    </>
  );
};

const OptionsWithSearch: React.FC<{
  AMList: any;
  isLoading?: boolean;
}> = ({ AMList, isLoading }) => {
  const { t } = useTranslation();

  const { selectedFilter, onSelect } = useFiltersContext();

  const handleSelect = (item: DropdownItem | null) => {
    if (!item) return;
    onSelect(item?.value, true, item?.code.toString());
  };

  const items = useMemo(() => {
    if (AMList?.body?.length) {
      return AMList.body.map((am: any) => ({
        code: am.nativeId,
        value: am.fullName,
      }));
    } else if (
      (selectedFilter === 'uplineDiamond' || selectedFilter === 'uplineEmerald') &&
      AMList?.body?.uplines?.length
    ) {
      return AMList?.body?.uplines?.map((am: any) => ({
        value: am.name,
        code: `${am.affiliateCode}-${am.aboNumber}`,
      }));
    }

    return [];
  }, [AMList]);

  return (
    <>
      <SearchDropdown
        dropdownName={t(`placeholder_search.${selectedFilter}`)}
        items={items}
        selectedItem={null}
        handleSelectItemCallback={handleSelect}
      />
      <OptionsWrapper AMList={AMList} isLoading={isLoading} />
    </>
  );
};

const OptionsWrapper: React.FC<{ AMList?: any; isLoading?: boolean }> = ({ AMList, isLoading }) => {
  const { filterState, onSelect, selectedFilter } = useFiltersContext();

  const activeFilterState = useMemo(
    () => filterState.find((st) => st.key === selectedFilter),
    [selectedFilter, filterState]
  );

  const apiNames = useMemo(() => {
    if (!AMList?.body) return [];
    switch (selectedFilter) {
      case 'primaryAM':
        return [
          {
            value: 'Unassigned Account Managers',
            code: 'unassigned',
          },
          {
            value: 'All Account Managers',
            code: 'assigned',
          },
          ...(Array.isArray(AMList?.body)
            ? AMList?.body?.map((am: any) => ({
                value: am.fullName,
                code: am.nativeId,
              }))
            : []),
        ];
      case 'caseEvaluator':
        return [
          {
            value: 'Unassigned Case Evaluators',
            code: 'unassigned',
          },
          {
            value: 'All Case Evaluators',
            code: 'assigned',
          },
          ...(Array.isArray(AMList?.body)
            ? AMList?.body?.map((am: any) => ({
                value: am.fullName,
                code: am.nativeId,
              }))
            : []),
        ];
      case 'amEmerald':
        return AMList?.body
          ? AMList?.body?.map((am: any) => ({
              value: am.fullName,
              code: am.nativeId,
            }))
          : [];

      case 'uplineDiamond':
      case 'uplineEmerald':
        return AMList?.body
          ? AMList?.body?.uplines?.map((am: any) => ({
              value: am.name,
              code: `${am.affiliateCode}-${am.aboNumber}`,
            }))
          : [];

      case 'state':
      case 'language':
      case 'marketName':
      case 'trackingAward':
      case 'currentAward':
        return AMList?.body
          ? AMList?.body?.options?.map((am: any) => ({
              value: am.value,
              code: am.code,
            }))
          : [];
      case 'loa':
        return AMList?.body
          ? AMList?.body?.map((am: any) => ({
              value: am.loaName,
              code: am.loaCode,
            }))
          : [];
      default:
        return [];
    }
  }, [selectedFilter, AMList]);

  const options = useMemo(
    () =>
      mapOptionsFromState(
        apiNames ?? [],
        (activeFilterState?.values ?? []).map((val: any) => val)
      ),
    [activeFilterState?.values, apiNames]
  );

  const shouldShowCode = useMemo(() => {
    return selectedFilter === 'caseEvaluator' || selectedFilter === 'primaryAM';
  }, [selectedFilter]);

  if (isLoading) {
    return (
      <div className={styles.options_wrapper}>
        {Array(8)
          .fill(0)
          .map((_, index) => (
            <div key={index} className={`${styles.dropdown_item} ${styles.loader} animate`}></div>
          ))}
      </div>
    );
  }

  return (
    <div className={styles.options_wrapper}>
      {options.map((option, i) => (
        <div className={styles.option} key={option.code}>
          <Checkbox
            label={
              shouldShowCode && option.code !== 'unassigned' && option.code !== 'assigned'
                ? `${option.value} - ${option.code}`
                : option.value
            }
            checked={option.checked}
            checkboxHandler={(checked) => {
              onSelect(option?.value, checked, option.code);
            }}
          />
        </div>
      ))}
    </div>
  );
};

type DropdownItem = {
  code: number | string;
  value: string;
};

interface SearchDropdownProps {
  dropdownName: string;
  items: DropdownItem[] | undefined;
  selectedItem: DropdownItem | null;
  handleSelectItemCallback: (docType: DropdownItem | null) => void;
  isLoading?: boolean;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  dropdownName,
  items,
  handleSelectItemCallback,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);

  const handleSelectItem = (item: DropdownItem) => {
    handleSelectItemCallback(item);
    setIsOpen(false);
    setSearch('');
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    setSearch(value);
    setFilteredItems(filterItemsBySearch(value));
  };

  const filterItemsBySearch = useCallback(
    (searchValue: string) => {
      if (!items) return [];
      return items.filter((item) => item.value.toLowerCase().includes(searchValue.toLowerCase()));
    },
    [items]
  );

  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  const renderItems = () => {
    if (isLoading) {
      const arr = new Array(5).fill(0);
      return arr.map((_, index) => (
        <div key={index} className={`${styles.dropdown_item} ${styles.loader} animate`}></div>
      ));
    }

    if (!items?.length) {
      return (
        <div className={`${styles.dropdown_item} ${styles.message}`}>
          {t('dropdown_items_not_available')}
        </div>
      );
    }
    if (!filteredItems?.length) {
      return (
        <div className={`${styles.dropdown_item} ${styles.message}`}>
          {t('dropdown_search_not_found')}
        </div>
      );
    }

    return filteredItems.map((item, index) => (
      <div
        key={item?.code ?? index}
        className={`dropdown_item ${styles.dropdown_item}`}
        onClick={() => handleSelectItem(item)}
      >
        {item?.value ?? ''}
      </div>
    ));
  };

  return (
    <div ref={dropdownRef} className={`dropdown ${styles.dropdown}`}>
      <div className={`${styles.input_wrapper}`} data-testid="toggle_drodpwn_testId">
        <input
          type="text"
          className={`dropdown_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
          value={search}
          onChange={handleSearch}
          data-testid="dropdown_input_testId"
          placeholder={dropdownName}
          onFocus={() => setIsOpen(true)}
        />
        <Icon
          name="search"
          size={24}
          color={currentTheme === 'light' ? '#2c2c2c' : '#AEAEB2'}
          className={styles.search_icon}
        />
      </div>

      {isOpen && (
        <div
          className={`dropdown_menu ${styles.dropdown_menu} ${isLoading ? styles.dropdown_loader : ''}`}
          data-testid="drodpwn_list_testId"
        >
          {renderItems()}
        </div>
      )}
    </div>
  );
};

export default FilterOptions;
